<template>
  <div class="parts-details-card-main-actions">
    <f7-button
      v-if="shouldShowPause"
      fill
      small
      color="pink"
      @click="handlePauseAction"
    >
      {{ $t('common.pause') }}
    </f7-button>

    <f7-button
      v-else-if="shouldShowResume"
      fill
      small
      color="pink"
      @click="handleResumeAction"
    >
      {{ $t('common.resume') }}
    </f7-button>

    <f7-button
      v-if="shouldShowEvaluate"
      fill
      small
      color="green"
      @click="handleEvaluation"
    >
      Valorar
    </f7-button>

    <!-- Popup: Select work position -->
    <PCSelectWorkPositionPopup
      v-model:opened="isSelectWorkPositionPopupOpened"
      @apply="handleWorkPositionSelection"
    />

    <!-- Popup: Select workers -->
    <PCSelectItemsPopup
      v-model:opened="isSelectWorkersPopupOpened"
      :items="selectWorkersData"
      :min="1"
      title="Seleccionar trabajadores"
      item-title-prop="Sujeto.NombreCompleto"
      @apply="handleWorkersSelection"
    />
  </div>
</template>

<script setup>
// @ts-check
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairParts } from '@/composables/useRepairParts';
import { useRepairPartsPauseResume } from '@/composables/useRepairPartsPauseResume';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { useParamRef } from '@erpagroapp/params';
import { SelectionActions } from '@/js/enums/externalRepairParts';
import { moveItemToFrontByProp } from '@/js/utils/moveItemToFrontByProp';

import { ConfirmDialogGH, HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { f7 } from 'framework7-vue';
import { logError } from '@/js/utils/log';
import { callGenerateRealHoursConsumptions } from '@/js/services/repairs/ApiRepairs';

import PCSelectItemsPopup from '@/components/common/PCSelectItemsPopup.vue';
import PCSelectWorkPositionPopup from '../PCSelectWorkPositionPopup.vue';

/** @import { PuestoTrabajo, Trabajador } from '@erpagroapp/types' */

const {
  workPartDetails,
  isUserResponsibleWorkPart,
  fetchWorkPartDetails,
} = useRepairParts();

const {
  isSelectWorkersPopupOpened,
  selectWorkersData,
  selectedWorkers,
  selectedAction,
  isSelectWorkPositionPopupOpened,
  selectedWorkPosition,
  registerPauseAction,
  handlePauseActionNextStep,
  registerResumeAction,
  handleResumeActionNextStep,
} = useRepairPartsPauseResume();

const { worker } = storeToRefs(useRepairPartsStore());

const partStatusStarted = useParamRef('EstadoParteReparacionEnCliente');
const partStatusValued = useParamRef('EstadoValoracionDefectoParteReparacion');

const isUserResponsible = computed(() => !!workPartDetails.value
  && isUserResponsibleWorkPart(workPartDetails.value.Id_TrabajadorPropietario));

const shouldShowPause = computed(() => !!workPartDetails.value
  && workPartDetails.value.Codigo_ParteProduccionEstado === partStatusStarted.value
  && !!workPartDetails.value.TieneIntervencionActiva);

const shouldShowResume = computed(() => !!workPartDetails.value
  && workPartDetails.value.Codigo_ParteProduccionEstado === partStatusStarted.value
  && !shouldShowPause.value);

const shouldShowEvaluate = computed(() => !!workPartDetails.value
  && new Set([partStatusStarted.value, partStatusValued.value]).has(workPartDetails.value.Codigo_ParteProduccionEstado)
  && isUserResponsible.value);

async function handlePauseAction() {
  if (!workPartDetails.value) {
    return;
  }

  const repairPartData = await handlePauseActionNextStep(
    workPartDetails.value.Id_ParteProduccion,
    workPartDetails.value.NumeroTrabajadoresActivos,
  );

  if (!repairPartData) {
    return;
  }

  const successful = await registerPauseAction(repairPartData, {
    preloaderMsg: 'Pausando el parte...',
    successMsg: 'El parte se ha pausado correctamente',
    errorMsg: 'No se ha podido pausar el parte',
  });

  if (successful) {
    fetchWorkPartDetails(workPartDetails.value.Id_ParteProduccion);
  }
}

/** A method that is used to handle the 'Resume Route' functionality */
async function handleResumeAction() {
  if (!workPartDetails.value) {
    return;
  }

  const repairPartData = await handleResumeActionNextStep(
    workPartDetails.value.Id_ParteProduccion,
    workPartDetails.value.NumeroTrabajadoresActivos,
    workPartDetails.value.NumeroTrabajadoresPrevistos,
    true,
  );

  if (!repairPartData) {
    return;
  }

  const successful = await registerResumeAction(repairPartData, {
    preloaderMsg: 'Reanudando el parte...',
    successMsg: 'El parte se ha reanudado correctamente',
    errorMsg: 'No se ha podido reanudar el parte',
  });

  if (successful) {
    fetchWorkPartDetails(workPartDetails.value.Id_ParteProduccion);
  }
}

/**
 * Handles the selection of work position in the 'Select the work position' popup.
 * @param {PuestoTrabajo} workPosition - The work position.
 */
function handleWorkPositionSelection(workPosition) {
  selectedWorkPosition.value = workPosition;

  if (selectedAction.value === SelectionActions.PAUSE) {
    handlePauseAction();
  } else if (selectedAction.value === SelectionActions.RESUME) {
    handleResumeAction();
  }
}

/**
 * Handles the selection of workers in the 'Select the part workers' popup.
 * @param {Trabajador[]} workers - A list of workers.
 */
function handleWorkersSelection(workers) {
  if (!worker.value) {
    return;
  }

  selectedWorkers.value = moveItemToFrontByProp(workers, worker.value.Id, 'Id');

  if (selectedAction.value === SelectionActions.PAUSE) {
    handlePauseAction();
  } else if (selectedAction.value === SelectionActions.RESUME) {
    handleResumeAction();
  }
}

/**
 * Generates real hours consumptions.
 * @param {number} workPartId - The ID of the work part.
 * @param {boolean} travel - Whether to include travel.
 */
async function generateRealHoursConsumptions(workPartId, travel) {
  let successful = false;
  HiDialog.Preloader(true, 'Valorando el parte...');

  try {
    const { data } = await callGenerateRealHoursConsumptions(
      workPartId,
      travel,
    );

    if (data.Valor === true) {
      successful = true;
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido valorar el parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido valorar el parte');
  } finally {
    HiDialog.Preloader(false);
  }

  return successful;
}

async function handleEvaluation() {
  if (!workPartDetails.value) {
    return;
  }

  if (workPartDetails.value.NumeroTrabajadoresActivos > 0) {
    HiNotification.Toast('El parte aún tiene intervenciones abiertas que deben ser cerradas antes de ser valorado');
    return;
  }

  const shouldContinue = await ConfirmDialogGH(
    'Esta acción empezará el proceso de valoración del parte',
    '¿Quiere continuar?',
  );

  if (!shouldContinue) {
    return;
  }

  let travel = false;

  if (workPartDetails.value.TrabajosEnDomicilio) {
    const travelPromise = new Promise((resolve) => {
      const warehouseValueDialog = f7.dialog.create({
        title: 'Eliminar desplazamiento',
        text: 'Seleccione si quiere eliminar el desplazamiento',
        buttons: [
          {
            text: 'CON DESP.',
            onClick() {
              resolve(true);
            },
          },
          {
            text: 'SIN DESP.',
            onClick() {
              resolve(false);
            },
          },
        ],
      });

      warehouseValueDialog.open();
    });

    travel = await travelPromise;
  }

  const workPartId = workPartDetails.value.Id_ParteProduccion;
  const valueSuccess = await generateRealHoursConsumptions(workPartId, travel);
  if (!valueSuccess) {
    return;
  }

  f7.views.main.router.navigate('/repairs/repairParts/finishReport', {
    props: {
      workPartId,
    },
  });

  // Update the details in background so if user goes back to this page, information is updated
  fetchWorkPartDetails(workPartDetails.value.Id_ParteProduccion);
}
</script>

<style scoped lang="scss">
.parts-details-card-main-actions {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
</style>
